import { useEffect, useState } from "react";
import { usePlayerStore } from "@/stores/playerStore";
import { canonicalJoin, cancelJoinRequest, knockOnLobby, normaliseCode } from "./lobby";
import { joinRequestPath, publicPath, rosterEntryPath, playerPath, presencePath, resultPath, revealAckPath } from "./paths";
import type { RoomBackend } from "./backend";
import { decodeJoinRequest, decodeLeaveRequest, decodePlayerResult, decodeRosterEntry, decodeSelfSnapshot, decodePublicSnapshot, reportSnapshotProblem, SnapshotValidationError } from "./snapshots";
import { REVEAL_TOKEN_PATTERN } from "@/stores/schemas";
import { decodeSession, isTransient, leavePath, lifecycleMessage, LifecycleError, outcomePath, requireActiveSession, retryTransient, sessionPath, travelerChoicePath } from "./lifecycle";
import { TRAVELERS } from "@/data/travelers";
import type { RoleId } from "@/stores/types";

/** Explicit URL intent wins. An empty ?join= opens the join form; the same
 * nonempty code may resume only the same authenticated user's saved session. */
export function applyJoinIntent(explicitCode: string | undefined, uid: string): boolean {
  const saved = usePlayerStore.getState();
  if (saved.uid !== uid || (explicitCode !== undefined && (!normaliseCode(explicitCode) || normaliseCode(explicitCode) !== saved.code))) {
    saved.reset(); return false;
  }
  return !!saved.code;
}
export async function joinLobby(backend: RoomBackend, code: string, uid: string, name: string): Promise<void> {
  const ps = usePlayerStore.getState();
  ps.reset();
  ps.setStatus("knocking");
  try {
    const canonical = canonicalJoin(code, name);
    const abort = new AbortController();
    await retryTransient(async () => {
      await requireActiveSession(backend, canonical.code);
      const outcome = await backend.get(outcomePath(canonical.code, uid));
      if (outcome != null) {
        if (outcome !== "rejected" && outcome !== "revoked") throw new SnapshotValidationError();
        throw new LifecycleError(outcome, outcome === "rejected" ? "Your join request was rejected." : "Removed from lobby.");
      }
      await knockOnLobby(backend, canonical.code, uid, canonical.name);
    }, abort.signal);
    ps.setSession({ code: canonical.code, uid, requestedName: canonical.name });
    ps.setStatus("waiting");
  } catch (error) {
    ps.setStatus(error instanceof LifecycleError && ["notFound", "rejected", "revoked"].includes(error.kind) ? error.kind as "notFound" | "rejected" | "revoked" : "error", lifecycleMessage(error));
    // ASTRA-10H-007: joining an already-ended lobby has no seat or session
    // context to read a result with -- a completed, generic Game Ended, never
    // an endless "Reading the final result...".
    if (error instanceof LifecycleError && error.kind === "ended") ps.setEnded({ status: "none" });
  }
}

export async function leaveLobby(backend: RoomBackend) {
  const ps = usePlayerStore.getState();
  if (!ps.code || !ps.uid) { ps.reset(); return; }
  // A local cached ID is never authority: inspect the server binding.
  const binding = decodeRosterEntry(await backend.get(rosterEntryPath(ps.code, ps.uid)));
  if (binding.status === "invalid") throw new SnapshotValidationError();
  if (binding.status === "ready") {
    await backend.set(leavePath(ps.code, ps.uid), true);
    ps.setStatus("leaving");
  } else {
    await cancelJoinRequest(backend, ps.code, ps.uid);
    ps.reset();
  }
}

/**
 * Player-side self-write of a chosen Traveler character (Phase 9 Setup
 * finalization B4). Mirrors leaveLobby's shape: a self-scoped Firebase
 * write, never a direct mutation of authoritative Storyteller state. The
 * Storyteller's live client observes this and applies it through the
 * Role seam (resolveRoles), bound to the participation the authoritative roster
 * record names -- the request itself carries no ParticipantId; see
 * applyTravelerChoice in membershipCommands.ts. Restricted client-side to the supported Traveler
 * catalogue as defense in depth; Firebase rules enforce the same
 * restriction server-side.
 *
 * Phase 10D (CLOSURE-01, Sol-amended): a submitted request is immutable for
 * the player until the Storyteller or the membership lifecycle clears it. The
 * rules admit only a first request or a same-value resubmit; a different
 * Traveler, or deleting it, is refused server-side. The UI shows the pending
 * choice instead of offering another (useOwnTravelerChoice).
 */
export async function chooseTraveler(backend: RoomBackend, roleId: RoleId): Promise<void> {
  const ps = usePlayerStore.getState();
  if (!ps.code || !ps.uid) throw new LifecycleError("cancelled", "Not connected to a lobby.");
  if (!TRAVELERS.some(t => t.id === roleId)) throw new Error("Not a supported Traveler character.");
  await backend.set(travelerChoicePath(ps.code, ps.uid), roleId);
}

/**
 * Phase 10D (CLOSURE-01, Sol-amended): this player's OWN pending Traveler
 * request -- a catalogue RoleId -- or null while none is pending. A self-scoped
 * read (the rules let a uid read only its own request); anything absent,
 * unreadable or not a catalogue id reads as null. Display only: the server
 * rule, not this view, is what refuses a replacement.
 */
export function useOwnTravelerChoice(backend: RoomBackend | null, enabled: boolean): RoleId | null {
  const code = usePlayerStore(s => s.code);
  const uid = usePlayerStore(s => s.uid);
  const [choice, setChoice] = useState<RoleId | null>(null);
  useEffect(() => {
    if (!backend || !code || !uid || !enabled) return;
    const off = backend.subscribe(travelerChoicePath(code, uid), value => {
      setChoice(typeof value === "string" && TRAVELERS.some(t => t.id === value) ? value : null);
    }, () => setChoice(null));
    return () => { off(); setChoice(null); };
  }, [backend, code, uid, enabled]);
  return enabled ? choice : null;
}

/**
 * Phase 10H (contract §12, F3): the player's explicit "I've Seen My Role" --
 * stores the reveal token they are being shown NOW at their own
 * revealAcks/{uid}. Advisory only: it is never game state and never gates
 * anything; the Storyteller derives Viewed by equality with the CURRENT token,
 * so a stale or delayed acknowledgement simply reads as not viewed. Refused
 * when there is nothing current to acknowledge.
 */
export async function acknowledgeReveal(backend: RoomBackend): Promise<void> {
  const ps = usePlayerStore.getState();
  if (!ps.code || !ps.uid || ps.status !== "seated") throw new LifecycleError("cancelled", "Not seated in a live game.");
  const token = ps.self?.revealToken;
  if (!token || !REVEAL_TOKEN_PATTERN.test(token)) throw new Error("There is no role to acknowledge yet.");
  await backend.set(revealAckPath(ps.code, ps.uid), token);
  // Optimistic local view; the subscription confirms the stored value.
  if (usePlayerStore.getState().self?.revealToken === token) usePlayerStore.getState().setOwnRevealAck(token);
}

export function usePlayerSync(backend: RoomBackend | null, retry = 0) {
  const code = usePlayerStore(s => s.code);
  const uid = usePlayerStore(s => s.uid);
  useEffect(() => {
    if (!backend || !code || !uid) return;
    return startPlayerHandshake(backend, code, uid);
  }, [backend, code, uid, retry]);
}

/** The only player handshake. Saved IDs are hints; subscriptions are installed
 * from validated server membership, never from localStorage. */
export function startPlayerHandshake(backend: RoomBackend, code: string, uid: string) {
  const abort = new AbortController();
  let active = true;
  let queued = false;
  let dirty = false;
  let bound: string | null = null;
  let publicSubscribed = false;
  let privateOff = () => {};
  let publicOff = () => {};
  let cancelPresence: (() => Promise<void>) | undefined;
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  const cleanups: (() => void)[] = [];
  const ps = () => usePlayerStore.getState();
  const current = () => active && ps().code === code && ps().uid === uid;
  /** Phase 10H: the terminal context outlives the live handshake. */
  const sameContext = () => ps().code === code && ps().uid === uid;
  // Phase 10H: public/status = ended was observed -- the terminal close is in
  // flight. Context is preserved and later reads that the teardown denies are
  // reconciled against the session rather than reported as errors.
  let ending = false;
  let ackOff = () => {};
  // Phase 10H (F2): every handshake -- reload, reconnect, re-attach -- re-seals.
  ps().setPlayerId(null); ps().setSelf(null); ps().setPublic(null); ps().setRevealed(false); ps().setOwnRevealAck(null);
  // ASTRA-10H-001: a REAL connection interruption also re-seals. A socket
  // reconnect does not rerun this handshake, so the backend's own connection
  // state is watched: once the connection has been established, a drop to
  // offline seals the card, and reconnecting leaves it sealed until the player
  // reveals again. The initial "not yet connected" is not a drop. The
  // acknowledgement is untouched -- an unchanged reveal token stays Viewed.
  if (backend.subscribeConnection) {
    let established = false;
    cleanups.push(backend.subscribeConnection((connected) => {
      if (!current()) return;
      if (connected) { established = true; return; }
      if (established) ps().setRevealed(false);
    }));
  }
  ps().setTerminalResult(null);
  ps().setRemoteData({ membership: "ready", request: "ready", self: "waiting", public: "waiting" });
  ps().setStatus("reconnecting");
  const stop = () => {
    active = false; abort.abort();
    cleanups.splice(0).forEach(off => off());
    privateOff(); publicOff(); ackOff(); clearInterval(heartbeat);
    void (async () => {
      try { await cancelPresence?.(); await backend.set(presencePath(code, uid), { online: false, lastSeen: Date.now() }); }
      catch { /* Disconnect handler or server expiry marks the device stale. */ }
    })();
  };
  const terminal = (kind: "rejected" | "revoked" | "notFound", message: string) => {
    if (!current()) return;
    ps().setSelf(null); ps().setPlayerId(null); ps().setPublic(null);
    ps().setRemoteData({ membership: "ready", request: "ready", self: "waiting", public: "waiting" });
    ps().setStatus(kind, message);
    stop();
  };
  /**
   * Phase 10H (contract §16): the session ended. The live subscriptions stop,
   * private identity is cleared, the terminal context (code, uid, seat,
   * session id, public view, Town notes) is preserved, and this player's own
   * results/{uid} is read and validated exactly:
   *  - a valid record naming the joined session -> the result screen;
   *  - absent (End Without Result, or not a participant) -> generic Game Ended;
   *  - malformed or another session's record -> never shown (generic);
   *  - a failed read -> retryable error, NEVER a false "no result".
   */
  const ended = (endedSessionId: string) => {
    if (!current()) return;
    stop();
    ps().setEnded({ status: "pending" });
    const expected = ps().sessionId ?? endedSessionId;
    void (async () => {
      try {
        const raw = await retryTransient(() => backend.get(resultPath(code, uid)), new AbortController().signal);
        if (!sameContext() || ps().status !== "ended") return;
        if (expected !== endedSessionId) { ps().setTerminalResult({ status: "none" }); return; }
        const decoded = decodePlayerResult(raw, expected);
        if (decoded.status === "ready") {
          ps().setTerminalResult({ status: "ready", result: { winner: decoded.data.winner, declaredAt: { ...decoded.data.declaredAt } } });
        } else {
          if (decoded.status === "invalid") reportSnapshotProblem("result", decoded.issues);
          ps().setTerminalResult({ status: "none" });
        }
      } catch (error) {
        if (!sameContext() || ps().status !== "ended") return;
        ps().setTerminalResult({ status: "error", message: lifecycleMessage(error) });
      }
    })();
  };
  const fail = (error: unknown) => {
    if (!current()) return;
    // Phase 10H: during the terminal close the teardown legitimately denies
    // private/public reads; the session decides, never a transient error.
    if (ending) { schedule(); return; }
    ps().setSelf(null); ps().setPublic(null);
    ps().setStatus("error", lifecycleMessage(error));
  };
  const watch = (path: string, receive: (value: unknown) => void) => {
    let off = () => {};
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const listen = () => {
      off = backend.subscribe(path, value => {
        if (!current()) return;
        try { receive(value); } catch (error) { fail(error); }
      }, error => {
        if (!current()) return;
        fail(error);
        if (isTransient(error) && attempts++ < 3) timer = setTimeout(() => { off(); listen(); }, 250 * 2 ** (attempts - 1));
        else schedule(); // Reconcile denied private/public reads with terminal membership/session.
      });
    };
    listen();
    return () => { off(); clearTimeout(timer); };
  };
  const startPresence = async () => {
    if (heartbeat || cancelPresence || !current()) return;
    try {
      cancelPresence = await backend.onDisconnectSet(presencePath(code, uid), { online: false, lastSeen: Date.now() });
      if (!current()) { await cancelPresence(); return; }
      const write = () => backend.set(presencePath(code, uid), { online: true, lastSeen: Date.now() });
      await write();
      if (!current()) return;
      heartbeat = setInterval(() => { void write().catch(fail); }, 15_000);
    } catch (error) { fail(error); }
  };
  /** Status while bound/waiting -- held at "ending" once the close began. */
  const live = (status: "seated" | "leaving" | "waiting" | "reconnecting") => ps().setStatus(ending ? "ending" : status);
  const reconcile = async () => {
    const session = decodeSession(await backend.get(sessionPath(code)));
    if (!current()) return;
    if (!session) { terminal("notFound", "This lobby does not exist or has expired."); return; }
    if (session.state === "ended") { ended(session.id); return; }
    // Phase 10H: remember which session this player is in (validated against
    // the terminal result later; persisted with the terminal context).
    if (ps().sessionId !== session.id) ps().setSessionId(session.id);
    const outcome = await backend.get(outcomePath(code, uid));
    if (!current()) return;
    if (outcome != null) {
      if (outcome !== "rejected" && outcome !== "revoked") throw new SnapshotValidationError();
      terminal(outcome, outcome === "rejected" ? "Your join request was rejected." : "Removed from lobby."); return;
    }
    let membership = decodeRosterEntry(await backend.get(rosterEntryPath(code, uid)));
    const request = decodeJoinRequest(await backend.get(joinRequestPath(code, uid)));
    if (membership.status === "waiting" && request.status === "waiting") membership = decodeRosterEntry(await backend.get(rosterEntryPath(code, uid)));
    if (!current()) return;
    if (membership.status === "invalid" || request.status === "invalid") throw new SnapshotValidationError();
    if (membership.status !== "ready" && request.status !== "ready") {
      // A request is only a pending artifact. Its disappearance (or a
      // transiently stale roster read) is not authoritative rejection or
      // revocation; those outcomes are written explicitly above. Keep the
      // handshake alive so an accepted seat can recover when its binding
      // becomes visible, regardless of listener ordering.
      if (ending) { live("reconnecting"); return; }
      if (ps().status !== "waiting" && ps().status !== "seated") ps().setStatus("reconnecting");
      return;
    }
    ps().setRemoteData({ membership: "ready", request: "ready" });
    if (membership.status === "ready") {
      const id = membership.data;
      ps().setPlayerId(id);
      // Phase 9C.3 (OPUS-003): the player's own leaveRequests/{uid} is the
      // authoritative signal for "leaving" vs "seated" while membership is
      // bound — this is what lets a Storyteller rejection (which clears only
      // this node) recover the player from the pending-leave screen back to
      // normal seated operation, and what lets a fresh reconnect show
      // "leaving" for a request that was already pending. Read fresh on
      // every reconcile pass, never cached, and never trusted once the
      // "revoked" outcome has already returned above.
      const leaveRequest = decodeLeaveRequest(await backend.get(leavePath(code, uid)));
      if (!current()) return;
      if (leaveRequest.status !== "ready") throw new SnapshotValidationError();
      live(leaveRequest.data ? "leaving" : "seated");
      if (bound !== id) {
        bound = id; privateOff(); ackOff(); ps().setSelf(null); ps().setRevealed(false); ps().setOwnRevealAck(null);
        privateOff = watch(playerPath(code, id), raw => {
          const self = decodeSelfSnapshot(raw);
          const next = self.status === "ready" ? self.data : null;
          // Phase 10H (F2, 10H-AC-031): a new reveal token -- a visible
          // identity change -- or a withdrawn identity re-seals the card.
          const previous = ps().self;
          if (!next || !previous || next.revealToken !== previous.revealToken) ps().setRevealed(false);
          ps().setSelf(next);
          ps().setRemoteData({ self: self.status });
        });
        // Phase 10H: this player's own advisory acknowledgement. Never a
        // failure path: an unreadable/malformed value reads as "none".
        ackOff = backend.subscribe(revealAckPath(code, uid), value => {
          if (!current()) return;
          ps().setOwnRevealAck(typeof value === "string" && REVEAL_TOKEN_PATTERN.test(value) ? value : null);
        }, () => { if (current()) ps().setOwnRevealAck(null); });
      }
    } else {
      // A missing binding is also ambiguous until the Storyteller writes the
      // explicit revoked outcome. Keep reconnecting rather than converting a
      // stale roster snapshot into a terminal removal.
      live(bound ? "reconnecting" : "waiting");
      if (request.status === "ready") usePlayerStore.setState({ requestedName: request.data });
    }
    if (!publicSubscribed) {
      publicSubscribed = true;
      publicOff = watch(publicPath(code), raw => {
        const decoded = decodePublicSnapshot(raw, code);
        if (decoded.status === "ended" || (decoded.status === "ready" && decoded.data.status === "ended")) {
          // Phase 10H (contract §16): the terminal close has begun. Show
          // "Ending..." with context preserved; the session's own end (watched
          // below) resolves to the result -- never a premature Game Ended.
          ending = true;
          if (ps().status !== "ended") ps().setStatus("ending");
          schedule();
          return;
        }
        ps().setPublic(decoded.status === "ready" ? decoded.data : null);
        ps().setRemoteData({ public: decoded.status });
      });
    }
    await startPresence();
  };
  function schedule() {
    if (!current()) return;
    dirty = true;
    if (queued) return;
    queued = true;
    void (async () => {
      try {
        while (dirty && current()) {
          dirty = false;
          await retryTransient(reconcile, abort.signal, () => { if (current()) ps().setStatus("reconnecting"); });
        }
      } catch (error) { fail(error); }
      finally { queued = false; }
    })();
  }
  // These non-private paths are independently authorized for this UID.
  for (const path of [sessionPath(code), outcomePath(code, uid), rosterEntryPath(code, uid), joinRequestPath(code, uid), leavePath(code, uid)]) cleanups.push(watch(path, schedule));
  schedule();
  return stop;
}
